import {
	Cancel as RejectedIcon,
	CheckCircle as PublicIcon,
	ChatBubbleOutline as CommentIcon,
	Close as CloseIcon,
	Delete as DeleteIcon,
	Groups as GroupsIcon,
	Lock as PrivateIcon,
	MoreVert as MoreIcon,
	Pending as PendingIcon,
	RateReview as EmptyIcon,
	Search as SearchIcon,
	Subject as SubjectIcon,
	ThumbUpOutlined as LikeIcon,
	Warning as WarningIcon,
} from "@mui/icons-material"
import { Avatar, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, Menu, MenuItem, Pagination, Paper, Select, Stack, Tab, Tabs, TextField, Typography } from "@mui/material"
import axios from "axios"
import { useEffect, useState } from "react"

export default function HomeworkSubmissions({ snackbar }) {
	const apiUrl = process.env.REACT_APP_API

	// ─── Filter option lists ────────────────────────────────────────────────
	const [batches, setBatches] = useState([])
	const [subjects, setSubjects] = useState([])
	const [homeworks, setHomeworks] = useState([])

	useEffect(() => {
		axios
			.get(`${apiUrl}/franchise/batch/getall?status=active&limit=100`)
			.then((res) => setBatches(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setBatches([]))
		axios
			.get(`${apiUrl}/franchise/homework/subject/getall?status=active&limit=100`)
			.then((res) => setSubjects(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setSubjects([]))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// ─── List State ───────────────────────────────────────────────────────────
	const [submissions, setSubmissions] = useState([])
	const [loading, setLoading] = useState(true)
	const [searchInput, setSearchInput] = useState("")
	const [search, setSearch] = useState("")
	const [statusTab, setStatusTab] = useState("pending")
	const [batchFilter, setBatchFilter] = useState("all")
	const [subjectFilter, setSubjectFilter] = useState("all")
	const [homeworkFilter, setHomeworkFilter] = useState("all")
	const [page, setPage] = useState(1)
	const [pagination, setPagination] = useState({
		current_page: 1,
		limit: 10,
		total_records: 0,
		total_pages: 1,
	})
	const [stats, setStats] = useState({ pending: 0, public: 0, private: 0, rejected: 0, total: 0 })

	// ─── homeworkid filter — scoped to the currently selected batch filter ────
	useEffect(() => {
		const params = new URLSearchParams()
		params.append("limit", "100")
		if (batchFilter !== "all") params.append("batch_id", batchFilter)

		axios
			.get(`${apiUrl}/franchise/homework/getall?${params.toString()}`)
			.then((res) => setHomeworks(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setHomeworks([]))

		setHomeworkFilter("all")
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [batchFilter])

	// ─── Menu / Dialog State ────────────────────────────────────────────────
	const [anchorEl, setAnchorEl] = useState(null)
	const [selectedSubmission, setSelectedSubmission] = useState(null)
	const [open, setOpen] = useState("") // "" | "reject" | "delete"
	const [action, setAction] = useState("") // "" | "public" | "private" | "reject" | "delete"
	const [rejectComment, setRejectComment] = useState("")
	const [lightboxImage, setLightboxImage] = useState(null)

	// ─── Comments Dialog State ──────────────────────────────────────────────
	const [commentsSubmission, setCommentsSubmission] = useState(null)
	const [comments, setComments] = useState([])
	const [commentsLoading, setCommentsLoading] = useState(false)
	const [deletingCommentId, setDeletingCommentId] = useState("")

	// ─── Fetch ────────────────────────────────────────────────────────────────
	const getSubmissions = () => {
		setLoading(true)
		const params = new URLSearchParams()
		if (search) params.append("search", search)
		if (statusTab !== "all") params.append("status", statusTab)
		if (batchFilter !== "all") params.append("batch_id", batchFilter)
		if (subjectFilter !== "all") params.append("subjectid", subjectFilter)
		if (homeworkFilter !== "all") params.append("homeworkid", homeworkFilter)
		params.append("page", page)

		axios
			.get(`${apiUrl}/franchise/homework/submission/getall?${params.toString()}`)
			.then((res) => {
				if (res.data.status === true) {
					setSubmissions(res.data.data || [])
					if (res.data.pagination) setPagination(res.data.pagination)
					if (res.data.stats) setStats(res.data.stats)
				} else {
					setSubmissions([])
				}
			})
			.catch(() => setSubmissions([]))
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		getSubmissions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, statusTab, batchFilter, subjectFilter, homeworkFilter, page])

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			setSearch(searchInput)
			setPage(1)
		}
	}

	const handlePageChange = (e, value) => {
		setPage(value)
		window.scrollTo({ top: 0, behavior: "smooth" })
	}

	// ─── Menu ─────────────────────────────────────────────────────────────────
	const openMenu = (event, submission) => {
		setAnchorEl(event.currentTarget)
		setSelectedSubmission(submission)
	}

	const closeMenu = () => {
		setAnchorEl(null)
	}

	// ─── Moderate (public / private) ───────────────────────────────────────────
	const moderate = (subStatus) => {
		setAction(subStatus)
		closeMenu()
		axios
			.post(`${apiUrl}/franchise/homework/submission/moderate`, { submissionid: selectedSubmission.submissionid, sub_status: subStatus }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Submission updated")
				if (res.data.status === true) getSubmissions()
			})
			.finally(() => setAction(""))
	}

	// ─── Reject ─────────────────────────────────────────────────────────────────
	const openReject = () => {
		setRejectComment("")
		setOpen("reject")
		closeMenu()
	}

	const handleReject = () => {
		setAction("reject")
		axios
			.post(
				`${apiUrl}/franchise/homework/submission/moderate`,
				{ submissionid: selectedSubmission.submissionid, sub_status: "rejected", sub_moderation_comment: rejectComment },
				{ headers: { "Content-Type": "application/json" } }
			)
			.then((res) => {
				snackbar && snackbar(res.data.message || "Submission rejected")
				if (res.data.status === true) {
					setOpen("")
					getSubmissions()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Delete ───────────────────────────────────────────────────────────────
	const openDelete = () => {
		setOpen("delete")
		closeMenu()
	}

	const handleDelete = () => {
		setAction("delete")
		axios
			.post(`${apiUrl}/franchise/homework/submission/delete`, { submissionid: selectedSubmission.submissionid }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Submission deleted")
				if (res.data.status === true) {
					setOpen("")
					getSubmissions()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Comments ─────────────────────────────────────────────────────────────
	const openComments = (row) => {
		setCommentsSubmission(row)
		setCommentsLoading(true)
		axios
			.get(`${apiUrl}/franchise/homework/submission/comment/getall?submissionid=${row.submissionid}`)
			.then((res) => setComments(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setComments([]))
			.finally(() => setCommentsLoading(false))
	}

	const closeComments = () => {
		setCommentsSubmission(null)
		setComments([])
	}

	const deleteComment = (commentid) => {
		setDeletingCommentId(commentid)
		axios
			.post(`${apiUrl}/franchise/homework/submission/comment/delete`, { commentid }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Comment deleted")
				if (res.data.status === true) {
					setComments((prev) => prev.filter((c) => c.commentid !== commentid))
					setSubmissions((prev) =>
						prev.map((s) => (s.submissionid === commentsSubmission?.submissionid ? { ...s, comment_count: Math.max(0, s.comment_count - 1) } : s))
					)
				}
			})
			.finally(() => setDeletingCommentId(""))
	}

	// ─── Helpers ──────────────────────────────────────────────────────────────
	const formatDate = (d) => {
		if (!d) return "--"
		return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
	}

	const getStatusColor = (status) => {
		switch (status) {
			case "pending":
				return "warning"
			case "public":
				return "success"
			case "private":
				return "info"
			case "rejected":
				return "error"
			default:
				return "default"
		}
	}

	const getStatusIcon = (status) => {
		switch (status) {
			case "pending":
				return <PendingIcon />
			case "public":
				return <PublicIcon />
			case "private":
				return <PrivateIcon />
			case "rejected":
				return <RejectedIcon />
			default:
				return null
		}
	}

	const isVideo = (row) => !!row.sub_video

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<Box className="content" pt={3} pb={5}>
			<Container maxWidth="lg">
				{/* ── Page Header ─────────────────────────────────── */}
				<Box mb={3}>
					<Typography variant="h5" fontWeight={700}>
						Home Work Moderation
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Review student submissions and control what appears on the public website
					</Typography>
				</Box>

				{/* ── Summary Strip ───────────────────────────────── */}
				<Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
					{[
						{ key: "all", label: "Total", value: stats.total, color: "text.primary" },
						{ key: "pending", label: "Pending", value: stats.pending, color: "warning.main" },
						{ key: "public", label: "Public", value: stats.public, color: "success.main" },
						{ key: "private", label: "Private", value: stats.private, color: "info.main" },
						{ key: "rejected", label: "Rejected", value: stats.rejected, color: "error.main" },
					].map((card) => (
						<Paper
							key={card.key}
							variant="outlined"
							onClick={() => {
								setStatusTab(card.key)
								setPage(1)
							}}
							sx={{
								px: 2.5,
								py: 1.5,
								borderRadius: 2,
								minWidth: 120,
								cursor: "pointer",
								borderWidth: statusTab === card.key ? 2 : 1,
								borderColor: statusTab === card.key ? card.color : "divider",
								transition: "border-color .15s, box-shadow .15s",
								"&:hover": { boxShadow: 2 },
							}}
						>
							<Typography variant="caption" color="text.secondary">
								{card.label}
							</Typography>
							<Typography variant="h6" fontWeight={700} color={card.color}>
								{card.value}
							</Typography>
						</Paper>
					))}
				</Stack>

				{/* ── Status Tabs ─────────────────────────────────── */}
				<Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
					<Tabs
						value={statusTab}
						onChange={(e, value) => {
							setStatusTab(value)
							setPage(1)
						}}
						variant="scrollable"
						scrollButtons="auto"
						sx={{ px: 1 }}
					>
						<Tab value="pending" label="Pending" />
						<Tab value="public" label="Public" />
						<Tab value="private" label="Private" />
						<Tab value="rejected" label="Rejected" />
						<Tab value="all" label="All" />
					</Tabs>
				</Paper>

				{/* ── Filters ─────────────────────────────────────── */}
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
						<TextField
							fullWidth
							size="small"
							placeholder="Search by student or title..."
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={handleSearchSubmit}
							sx={{ minWidth: 220 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" color="action" />
									</InputAdornment>
								),
							}}
						/>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Batch</InputLabel>
							<Select
								label="Batch"
								value={batchFilter}
								onChange={(e) => {
									setBatchFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Batches</MenuItem>
								{batches.map((b) => (
									<MenuItem key={b.batch_id} value={b.batch_id}>
										{b.batch_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Subject</InputLabel>
							<Select
								label="Subject"
								value={subjectFilter}
								onChange={(e) => {
									setSubjectFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Subjects</MenuItem>
								{subjects.map((s) => (
									<MenuItem key={s.subjectid} value={s.subjectid}>
										{s.subject_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 200 }}>
							<InputLabel>Assignment</InputLabel>
							<Select
								label="Assignment"
								value={homeworkFilter}
								onChange={(e) => {
									setHomeworkFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Assignments</MenuItem>
								{homeworks.map((h) => (
									<MenuItem key={h.homeworkid} value={h.homeworkid}>
										{h.hw_title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant="outlined"
							color="inherit"
							onClick={() => {
								setSearchInput("")
								setSearch("")
								setBatchFilter("all")
								setSubjectFilter("all")
								setHomeworkFilter("all")
								setPage(1)
							}}
						>
							Clear
						</Button>
					</Stack>
				</Paper>

				{/* ── Submission Cards ────────────────────────────── */}
				{loading ? (
					<Box textAlign="center" py={8}>
						<CircularProgress />
					</Box>
				) : submissions.length === 0 ? (
					<Paper variant="outlined" sx={{ py: 8, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
						<EmptyIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
						<Typography variant="h6" color="text.secondary">
							No submissions found
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Try adjusting your filters
						</Typography>
					</Paper>
				) : (
					<Grid container spacing={2}>
						{submissions.map((row) => (
							<Grid item xs={12} sm={6} md={4} key={row.submissionid}>
								<Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
									{/* Media */}
									<Box sx={{ position: "relative", bgcolor: "grey.200", height: 180 }}>
										{isVideo(row) ? (
											<video
												src={row.sub_video}
												controls
												controlsList="nodownload noremoteplayback"
												disablePictureInPicture
												style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
											/>
										) : row.sub_image ? (
											<img
												src={row.sub_image}
												alt={row.sub_title || "Home work submission"}
												onClick={() => setLightboxImage(row.sub_image)}
												style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "zoom-in" }}
											/>
										) : (
											<Box display="flex" alignItems="center" justifyContent="center" height="100%">
												<EmptyIcon sx={{ fontSize: 40, color: "text.disabled" }} />
											</Box>
										)}
										<IconButton
											size="small"
											onClick={(e) => openMenu(e, row)}
											sx={{ position: "absolute", top: 6, right: 6, bgcolor: "background.paper", "&:hover": { bgcolor: "background.paper" } }}
										>
											<MoreIcon fontSize="small" />
										</IconButton>
									</Box>

									<Box p={2} flexGrow={1}>
										<Stack direction="row" alignItems="center" spacing={1} mb={1}>
											<Avatar src={row.student_dp} sx={{ width: 32, height: 32 }}>
												{row.student_name?.charAt(0)?.toUpperCase()}
											</Avatar>
											<Box flexGrow={1} minWidth={0}>
												<Typography variant="body2" fontWeight={700} noWrap>
													{row.student_name}
												</Typography>
												<Typography variant="caption" color="text.secondary" noWrap display="block">
													{row.hw_title}
												</Typography>
											</Box>
											<Chip size="small" icon={getStatusIcon(row.sub_status)} label={row.sub_status} color={getStatusColor(row.sub_status)} sx={{ textTransform: "capitalize" }} />
										</Stack>

										<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
											<Chip size="small" variant="outlined" icon={<SubjectIcon fontSize="small" />} label={row.subject_name} />
											<Chip size="small" variant="outlined" icon={<GroupsIcon fontSize="small" />} label={row.batch_name} />
										</Stack>

										{row.sub_caption && (
											<Typography variant="body2" color="text.secondary" mb={1} sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
												{row.sub_caption}
											</Typography>
										)}

										{row.sub_status === "rejected" && row.sub_moderation_comment && (
											<Typography variant="caption" color="error.main" display="block" mb={1}>
												Reason: {row.sub_moderation_comment}
											</Typography>
										)}

										<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
											<Stack direction="row" spacing={2}>
												<Stack direction="row" spacing={0.5} alignItems="center">
													<LikeIcon fontSize="small" color="action" />
													<Typography variant="caption" color="text.secondary">
														{row.like_count}
													</Typography>
												</Stack>
												<Stack
													direction="row"
													spacing={0.5}
													alignItems="center"
													onClick={() => openComments(row)}
													sx={{ cursor: "pointer" }}
												>
													<CommentIcon fontSize="small" color="action" />
													<Typography variant="caption" color="text.secondary">
														{row.comment_count}
													</Typography>
												</Stack>
											</Stack>
											<Typography variant="caption" color="text.secondary">
												{formatDate(row.sub_created_at)}
											</Typography>
										</Stack>
									</Box>
								</Paper>
							</Grid>
						))}
					</Grid>
				)}

				{/* ── Pagination ──────────────────────────────────── */}
				{pagination.total_pages > 1 && (
					<Box mt={4} display="flex" justifyContent="center">
						<Pagination count={pagination.total_pages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
					</Box>
				)}
			</Container>

			{/* ── Per-card Menu ────────────────────────────────────── */}
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
				<MenuItem disabled={selectedSubmission?.sub_status === "public" || action !== ""} onClick={() => moderate("public")}>
					<PublicIcon fontSize="small" sx={{ mr: 1 }} color="success" />
					Make Public
				</MenuItem>
				<MenuItem disabled={selectedSubmission?.sub_status === "private" || action !== ""} onClick={() => moderate("private")}>
					<PrivateIcon fontSize="small" sx={{ mr: 1 }} color="info" />
					Make Private
				</MenuItem>
				<MenuItem onClick={openReject}>
					<RejectedIcon fontSize="small" sx={{ mr: 1 }} color="error" />
					Reject…
				</MenuItem>
				<Divider />
				<MenuItem onClick={openDelete} sx={{ color: "error.main" }}>
					<DeleteIcon fontSize="small" sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Menu>

			{/* ── Reject Dialog ───────────────────────────────────── */}
			<Dialog open={open === "reject"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					Reject Submission
					<IconButton size="small" onClick={() => setOpen("")}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<Divider />
				<DialogContent>
					<Typography variant="body2" color="text.secondary" mb={2} mt={1}>
						Let <strong>{selectedSubmission?.student_name}</strong> know why this submission was rejected so they can resubmit.
					</Typography>
					<TextField label="Reason" fullWidth required multiline minRows={3} autoFocus value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} />
				</DialogContent>
				<Divider />
				<DialogActions sx={{ p: 2 }}>
					<Button color="inherit" onClick={() => setOpen("")}>
						Cancel
					</Button>
					<Button variant="contained" color="error" disabled={action === "reject" || rejectComment.trim() === ""} onClick={handleReject}>
						{action === "reject" ? <CircularProgress size={20} color="inherit" /> : "Reject Submission"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Delete Confirm Dialog ───────────────────────────── */}
			<Dialog open={open === "delete"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
				<DialogContent>
					<Box textAlign="center">
						<WarningIcon sx={{ width: 64, height: 64 }} color="error" />
						<Typography color="error" fontSize={20} fontWeight={600} mt={1}>
							Delete this submission?
						</Typography>
						<Typography color="text.secondary" fontSize={13} mb={2}>
							<strong>{selectedSubmission?.student_name}</strong>'s submission for <strong>{selectedSubmission?.hw_title}</strong> will be permanently removed, including its media file. This action cannot be undone.
						</Typography>
					</Box>
					<Stack spacing={1}>
						<Button variant="contained" color="error" disabled={action === "delete"} onClick={handleDelete}>
							{action === "delete" ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
						</Button>
						<Button variant="outlined" color="inherit" onClick={() => setOpen("")}>
							Cancel
						</Button>
					</Stack>
				</DialogContent>
			</Dialog>

			{/* ── Comments Dialog ─────────────────────────────────── */}
			<Dialog open={Boolean(commentsSubmission)} fullWidth maxWidth="xs" onClose={closeComments}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					Comments
					<IconButton size="small" onClick={closeComments}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<Divider />
				<DialogContent sx={{ p: 0 }}>
					{commentsLoading ? (
						<Box textAlign="center" py={4}>
							<CircularProgress size={28} />
						</Box>
					) : comments.length === 0 ? (
						<Box textAlign="center" py={4}>
							<Typography variant="body2" color="text.secondary">
								No comments yet
							</Typography>
						</Box>
					) : (
						comments.map((c, i) => (
							<Stack key={c.commentid} direction="row" spacing={1.5} alignItems="flex-start" px={2} py={1.5} sx={{ borderTop: i === 0 ? "none" : "1px solid", borderColor: "divider" }}>
								<Avatar src={c.student_dp} sx={{ width: 32, height: 32 }}>
									{c.student_name?.charAt(0)?.toUpperCase()}
								</Avatar>
								<Box flexGrow={1} minWidth={0}>
									<Typography variant="body2" fontWeight={700}>
										{c.student_name}
									</Typography>
									<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
										{c.comment_text}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{formatDate(c.created_at)}
									</Typography>
								</Box>
								<IconButton size="small" disabled={deletingCommentId === c.commentid} onClick={() => deleteComment(c.commentid)}>
									{deletingCommentId === c.commentid ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color="error" />}
								</IconButton>
							</Stack>
						))
					)}
				</DialogContent>
			</Dialog>

			{/* ── Image Lightbox ──────────────────────────────────── */}
			<Dialog open={Boolean(lightboxImage)} onClose={() => setLightboxImage(null)} maxWidth="lg" PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none", overflow: "visible" } }}>
				<Box sx={{ position: "relative" }}>
					<IconButton
						onClick={() => setLightboxImage(null)}
						sx={{ position: "absolute", top: -16, right: -16, bgcolor: "background.paper", "&:hover": { bgcolor: "background.paper" } }}
					>
						<CloseIcon />
					</IconButton>
					<Box component="img" src={lightboxImage} alt="Home work submission" sx={{ display: "block", maxWidth: "90vw", maxHeight: "90vh", borderRadius: 2 }} />
				</Box>
			</Dialog>
		</Box>
	)
}
